/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import React, { useContext, useMemo } from 'react'

import { Autocomplete, Checkbox, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { useMaterialUIController } from 'context/materialUI'
import { FormContext } from 'layouts/feedback/internal/constants'
import { Feedback } from 'layouts/feedback/internal/types'
import FormField from 'modules/FormField'

function MemberOutcome(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { user } = useAuth()
	const formContext = useContext(FormContext)
	const { localForm, setFormEdited, setLocalForm, showError } = formContext
	const { configuration } = controller

	const STAGE = 3

	const sectionDisabled = useMemo(
		() =>
			(!ROLE_LEVEL[2].includes(user.data.role) && localForm.stage.current !== STAGE) ||
			user.data.role === 'VIEWER',
		[localForm.stage, user.data.role]
	)

	const errorVisible = localForm.stage.current >= STAGE && showError

	const memberOutcomes =
		configuration?.feedback?.outcomes.filter(
			item => item.memberType === localForm.memberType && item.feedbackType === localForm.type
		) ?? []

	const outcomeNameToId = Object.fromEntries(memberOutcomes.map(o => [o.name, o.id.toString()]))

	const handleFormUpdate = (value: string, field: keyof Feedback['form']['memberOutcome']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				memberOutcome: {
					...prev.form.memberOutcome,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id={'recipient-outcome'}>
			<MDBox display={'flex'} flexDirection={'row'} gap={2} p={3}>
				<MDAvatar bgColor="primary" size="xs">
					5
				</MDAvatar>
				<MDTypography variant="h5" fontWeight="medium">
					Member Outcome
				</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							disabled={sectionDisabled}
							variant="outlined"
							label="Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.memberOutcome?.datetime ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'datetime')
							}}
							error={errorVisible && !localForm.form.memberOutcome?.datetime}
							helperText={
								errorVisible && !localForm.form.memberOutcome?.datetime
									? 'Date/Time is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Autocomplete
							disableClearable
							readOnly={sectionDisabled}
							value={localForm.form.memberOutcome?.outcome?.name ?? null}
							options={memberOutcomes?.map(item => item.name) ?? []}
							noOptionsText="No outcomes set for this member"
							onChange={(_, newValue) => {
								const id = outcomeNameToId[newValue] ?? ''
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										memberOutcome: {
											...prev.form.memberOutcome,
											outcome: {
												id,
												name: newValue,
											},
										},
									},
								}))
							}}
							size="medium"
							renderInput={params => (
								<FormField
									{...params}
									label="Outcome"
									InputLabelProps={{ shrink: true }}
									error={errorVisible && !localForm.form.memberOutcome?.outcome}
									helperText={
										errorVisible && !localForm.form.memberOutcome?.outcome
											? 'Outcome is required'
											: ''
									}
								/>
							)}
						/>
					</Grid>
					{memberOutcomes.find(o => o.name === localForm.form.memberOutcome?.outcome?.name)
						?.has_monetary_value && (
						<Grid item xs={12} sm={4}>
							<FormField
								label="Monetary Value (£)"
								placeholder="15.52"
								autoComplete="off"
								disabled={sectionDisabled}
								value={localForm.form?.memberOutcome?.monetary_value?.toString() ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									handleFormUpdate(e.target.value, 'monetary_value')
								}}
								error={errorVisible && !localForm.form.memberOutcome?.monetary_value}
								helperText={
									errorVisible && !localForm.form.memberOutcome?.monetary_value
										? 'Monetary value is required'
										: ''
								}
							/>
						</Grid>
					)}
					<Grid item xs={12}>
						<TextField
							label="Details Of Outcome"
							multiline
							disabled={sectionDisabled}
							rows={5} // Adjust number of rows for the size of the textarea
							variant="outlined"
							placeholder="Please provide a description of the outcome."
							InputLabelProps={{ shrink: true }}
							fullWidth
							value={localForm.form.memberOutcome?.details ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'details')
							}}
							error={errorVisible && !localForm.form.memberOutcome?.details}
							helperText={
								errorVisible && !localForm.form.memberOutcome?.details ? 'Details required' : ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={8}>
						{/* Left Side Content (optional) */}
					</Grid>
					<Grid item xs={12} sm={4}>
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent={'flex-end'}
							flexDirection={'row'}
						>
							<MDTypography variant="h6" color="secondary">
								Member Outcome Complete:
							</MDTypography>
							<Checkbox
								checked={localForm.stage.current > STAGE}
								disabled={localForm.stage.min > STAGE}
								onChange={() => {
									const increment = localForm.type === 'COMMENDATION' ? 2 : 1
									setLocalForm(prev => ({
										...prev,
										stage: {
											...prev.stage,
											current: localForm.stage.current > STAGE ? STAGE : STAGE + increment,
										},
									}))
									setFormEdited(true)
								}}
							/>
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default MemberOutcome
